import { AccommodationData } from '~/models/Product'
import { ApiLocation } from '../spot/apiTypes'

type ApiProduct =
	| ApiTripProduct
	| ApiAccommodationProduct
	| ApiActivityProduct
	| ApiTransportProduct

type BaseApiProduct = {
	id: string
	title: string
	description?: string
	category: API_PRODUCT_CATEGORY
	sub_category?: API_PRODUCT_SUB_CATEGORY
	prices: ApiPrice[]
	external_url: string
	cover_urls: string[]
	is_favorite: boolean
	languages: string[]
	provider?: ApiProvider
}

type ApiTripProduct = BaseApiProduct & {
	category: API_PRODUCT_CATEGORY.TRIP
	data?: Record<API_PRODUCT_DATA, number>
	markers?: {
		label: string
		location: ApiLocation
	}[]
	itinerary?: {
		day: number
		destinations: {
			name: string
			cover: string
			description: string
		}[]
		products?: ApiProduct[]
	}[]
}

type ApiAccommodationProduct = BaseApiProduct & {
	category: API_PRODUCT_CATEGORY.ACCOMMODATION
	subcategory?: API_ACCOMMODATION_SUBCATEGORY
	data?: AccommodationData
}

type ApiActivityProduct = BaseApiProduct & {
	category: API_PRODUCT_CATEGORY.ACTIVITY
	data?: ApiActivityData
}

type ApiTransportProduct = ApiRentalTransportProduct | ApiOtherTransportProduct

type ApiRentalTransportProduct = BaseApiProduct & {
	category: API_PRODUCT_CATEGORY.TRANSPORT
	subcategory?: API_TRANSPORT_SUBCATEGORY.RENTAL
	data?: ApiTransportRentalData
}

type ApiOtherTransportProduct = BaseApiProduct & {
	category: API_PRODUCT_CATEGORY.TRANSPORT
	subcategory?: Exclude<API_TRANSPORT_SUBCATEGORY, API_TRANSPORT_SUBCATEGORY.RENTAL>
	data?: ApiTransportData
}

type ApiActivityData = {
	start_date?: string
	duration?: number
	score?: number
	reviews?: number
	guide_languages?: string[]
	free_cancellation_policy?: string
	accessibility_wheelchair?: boolean
	included?: string[]
	not_included?: string[]
	meeting_point?: ApiLocation
	advance_minutes?: number
}

type ApiTransportData = {
	company: string
	duration: string
	baggage_info: string
	departure: string
	arrival: string
	origin_destination_code: string
	target_destination_code: string
	origin_airport_name: string
	target_airport_name: string
	day_difference: number
	num_segments: number
}

type ApiTransportRentalData = {
	company: string
	pickup_location: string
	dropoff_location: string
}

export type ApiPrice = { currency: string; price: number; type?: 'per_person' | 'total' }

export type ApiProvider = {
	id: string
	name: string
	logo?: string
}

export enum API_PRODUCT_DATA {
	ADULTS = 'adults',
	CHILDREN = 'children',
	DESTINATIONS = 'destinations',
	ACCOMMODATIONS = 'accommodations',
	NIGHTS = 'nights',
	TRANSFERS = 'transfers',
	TOURS = 'tours',
	TRANSPORTS = 'transports',
}

export type ApiActivity = {
	id: string
	title: string
	currency: string
	price: number
	url: string
	photo: string
	description: string
	duration: number
	location: ApiLocation
	reviews: number
	score: number
}

enum API_PRODUCT_CATEGORY {
	TRIP = 'trip',
	ACCOMMODATION = 'accommodation',
	ACTIVITY = 'activity',
	TRANSPORT = 'transport',
}

enum API_PRODUCT_SUB_CATEGORY {
	FLIGHT = 'flight',
	RENTAL = 'rental',
	HOTEL = 'hotel',
}

enum API_ACCOMMODATION_SUBCATEGORY {
	HOTEL = 'hotel',
	APARTMENT = 'apartment',
}

enum API_TRANSPORT_SUBCATEGORY {
	RENTAL = 'rental',
	TAXI = 'taxi',
	FLIGHT = 'flight',
	BOAT = 'boat',
	TRAIN = 'train',
}

export {
	ApiProduct,
	ApiTripProduct,
	ApiAccommodationProduct,
	ApiActivityProduct,
	ApiTransportProduct,
	ApiRentalTransportProduct,
	ApiOtherTransportProduct,
	API_PRODUCT_CATEGORY,
	API_TRANSPORT_SUBCATEGORY,
	API_ACCOMMODATION_SUBCATEGORY,
	API_PRODUCT_SUB_CATEGORY,
}
